'use client';
import IconTriangleWarning from '@mnd-frontend/icons/components/TriangleWarning';
import {
  GREY_400,
  GREY_600,
  RADIUS_SM,
  RED_700,
  SPACING_0_5,
} from '@mnd-frontend/ui/shared/tokens';
import {
  type ChangeEventHandler,
  type ForwardedRef,
  forwardRef,
  useCallback,
  useId,
  useState,
} from 'react';
import styled, { css } from 'styled-components';
import type { InputBaseProps, InputWrapperProps } from './index.type';

const labelPaddingCalc = (
  withLeadingIcon: boolean,
  withTrailingIcon: boolean,
  ignoreTrailing = false,
) => {
  const leadingExtra = withLeadingIcon ? 2 : 0;
  const trailingExtra = withTrailingIcon && !ignoreTrailing ? 2 : 0;
  return `${1.75 + leadingExtra + trailingExtra}rem`;
};

type LabelProps = {
  $hasContent: boolean;
  $hasError: boolean;
  $withLeadingIcon: boolean;
  $withTrailingIcon: boolean;
  $defaultLabelTopPos: string;
  disabled?: boolean;
};

const moveTop = css<{
  $withLeadingIcon: boolean;
  $withTrailingIcon: boolean;
}>`
  top: 0;
  left: 0.625rem;
  z-index: 1;
  font-size: 0.75rem;
  line-height: 1.125rem;
  max-width: calc(
    100% - ${props => labelPaddingCalc(props.$withLeadingIcon, props.$withTrailingIcon, true)}
  );
`;

const LeadingIconWrapper = styled.span`
  color: ${props => props.theme.colorIconsDefault};
  position: absolute;
  left: 1rem;
  top: 50%;
  line-height: 0;
  transform: translateY(-50%);
  pointer-events: none;
`;

const TrailingIconWrapper = styled.span`
  color: ${props => props.theme.colorIconsDefault};
  position: absolute;
  right: 1rem;
  top: 50%;
  line-height: 0;
  transform: translateY(-50%);
  pointer-events: none;
`;
const InputLabel = styled.label<LabelProps>`
  user-select: none;
  font-size: 1rem;
  line-height: 1.25rem;
  border-radius: ${RADIUS_SM};
  color: ${props => {
    if (props.$hasContent) {
      if (props.$hasError) return props.theme.colorTextInputLabelPopulatedEnabledError;
      if (props.disabled) return GREY_600;
      return props.theme.colorTextInputLabelPopulatedEnabledDefault;
    }
    if (props.$hasError) return props.theme.colorTextInputLabelEmptyEnabledError;
    if (props.disabled) return GREY_600;
    return props.theme.colorTextInputLabelEmptyEnabledDefault;
  }};
  background: var(--current-background, white);
  padding: 0 0.25rem;
  transition: all 0.2s;
  position: absolute;
  font-weight: normal;
  left: ${props => (props.$withLeadingIcon ? '2.625rem' : '0.625rem')};
  max-width: calc(
    100% - ${props => labelPaddingCalc(props.$withLeadingIcon, props.$withTrailingIcon)}
  );
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  top: ${props => props.$defaultLabelTopPos};
  z-index: 0;
  transform: translateY(-50%);
  pointer-events: none;

  ${props => props.$hasContent && moveTop}
`;

const StyledInput = styled.input<{
  $withLeadingIcon: boolean;
  $withTrailingIcon: boolean;
}>`
  position: relative;
  z-index: 1;
  background: transparent;
  border-radius: ${props => props.theme.radiusInput};
  border: 0;
  width: 100%;
  font-weight: normal;
  font-size: 1rem;
  outline: 0;
  padding: 0.5rem 0.875rem;
  padding-left: ${props => props.$withLeadingIcon && props.theme.sizingIconMd};
  padding-right: ${props => props.$withTrailingIcon && props.theme.sizingIconMd};
  &[disabled] {
    color: ${GREY_600};
  }
`;

const InputWrapperStyles = styled.div<{
  $overflowY?: boolean;
  $hasError: boolean;
  disabled?: boolean;
  $withLeadingIcon: boolean;
  $withTrailingIcon: boolean;
  $hasContent: boolean;
  $hackForDarkBackgroundUseAndBeFired?: boolean;
}>`
  position: relative;
  z-index: 2;
  width: 100%;
  line-height: 1.5rem;
  ${props =>
    props.$overflowY &&
    css`
      overflow-y: auto;
      scrollbar-width: thin;
      height: 100%;
      &::-webkit-scrollbar {
        width: 0.5rem;
      }
      &::-webkit-scrollbar-thumb {
        background-color: ${({ theme }) => theme.colorBorderDividerDefault};
        border-radius: 1rem;
      }
      &::-webkit-scrollbar-track {
        background-color: transparent;
      }
    `}

  * {
    line-height: 1.5rem;
  }
  outline: 1px solid
    ${props => {
      if (props.$hasContent) {
        if (props.$hasError) return props.theme.colorBorderInputPopulatedEnabledError;
        if (props.disabled) return GREY_400;
        return props.theme.colorBorderInputPopulatedEnabledDefault;
      }
      if (props.$hasError) return props.theme.colorBorderInputEmptyEnabledError;
      if (props.disabled) return GREY_400;
      return props.theme.colorBorderInputEmptyEnabledDefault;
    }};
  color: ${props => props.theme.colorTextInputInputPopulatedEnabled};
  outline-offset: -1px;
  border-radius: ${props => props.theme.radiusInput};
  border: 2px inset transparent;
  &:focus-within ${InputLabel} {
    ${moveTop}
  }

  ${LeadingIconWrapper},
  ${TrailingIconWrapper} {
    color: ${props => props.disabled && GREY_600};
  }
  &:active,
  &:hover {
    color: ${props => props.theme.colorTextInputInputPopulatedHover};
    outline: 1px solid
      ${props => {
        if (props.$hasContent) {
          if (props.$hasError) return props.theme.colorBorderInputPopulatedHoverError;
          if (props.disabled) return GREY_400;
          return props.theme.colorBorderInputPopulatedHoverDefault;
        }
        if (props.$hasError) return props.theme.colorBorderInputEmptyHoverError;
        if (props.disabled) return GREY_400;
        return props.theme.colorBorderInputEmptyHoverDefault;
      }};
  }
  &:focus-within {
    color: ${props => props.theme.colorTextInputInputPopulatedFocused};
    outline: 2px solid
      ${props => {
        if (props.$hasContent) {
          if (props.$hasError) return props.theme.colorBorderInputPopulatedFocusedError;
          if (props.disabled) return GREY_400;
          return props.theme.colorBorderInputPopulatedFocusedDefault;
        }
        if (props.$hasError) return props.theme.colorBorderInputEmptyFocusedError;
        if (props.disabled) return GREY_400;
        return props.theme.colorBorderInputEmptyFocusedDefault;
      }};
    outline-offset: -2px;
    + ${InputLabel} {
      color: ${props => {
        if (props.$hasContent) {
          if (props.$hasError) return props.theme.colorTextInputLabelPopulatedFocusedError;
          if (props.disabled) return GREY_400;
          return props.theme.colorTextInputLabelPopulatedFocusedDefault;
        }
        if (props.$hasError) return props.theme.colorTextInputLabelEmptyFocusedError;
        if (props.disabled) return GREY_400;
        return props.theme.colorTextInputLabelEmptyFocusedDefault;
      }};
    }
  }

  ${props =>
    props.$hackForDarkBackgroundUseAndBeFired &&
    css`
      && {
        border: 0;
        outline: 0;
      }
    `}
`;

const InputError = styled.div`
  font-size: 0.75rem;
  line-height: 1.125rem;
  color: ${RED_700};
  display: flex;
  align-items: flex-start;
  gap: ${SPACING_0_5};
`;

const InputWarning = styled(InputError)`
  color: ${props => props.theme.colorTextInputWarningMessageDefault};
`;

const IconWrapper = styled.span`
  margin-top: 0.1rem;
  flex: none;
`;

const HelperText = styled.p`
  margin: 0;
  color: ${props => props.theme.colorTextInputHelperTextDefault};
  font-size: 0.75rem;
  line-height: 1.125rem;
`;

const ComponentWrapper = styled.div<{ $skipMargin?: boolean; $overflowY?: boolean }>`
  // Position absolute label might render above elements higher up on the page
  margin-top: ${props => !props.$skipMargin && '0.5rem'};
  ${props => props.$overflowY && css`overflow: hidden;`}
`;

const BottomRow = styled.div<{ $alignRight?: boolean }>`
  margin-top: 0.5rem;
  display: flex;
  gap: 0.5rem;
  justify-content: ${props => (props.$alignRight ? 'flex-end' : 'space-between')};
  align-items: center;
`;

export const InputWrapper = ({
  overflowY,
  id,
  label,
  labelId,
  hasContent,
  error,
  warning,
  helperText,
  leadingIcon,
  trailingIcon,
  children,
  skipMargin,
  disabled,
  characterCounterSlot,
  hackForDarkBackgroundUseAndBeFired,
  defaultLabelTopPos = '50%',
}: InputWrapperProps) => {
  const showHelperText = !(error || warning) && !!helperText;

  return (
    <ComponentWrapper $skipMargin={skipMargin} $overflowY={overflowY}>
      <InputWrapperStyles
        $overflowY={overflowY}
        $hasError={!!error}
        disabled={disabled}
        $withLeadingIcon={!!leadingIcon}
        $withTrailingIcon={!!trailingIcon}
        $hasContent={hasContent}
        $hackForDarkBackgroundUseAndBeFired={hackForDarkBackgroundUseAndBeFired}
      >
        {leadingIcon && <LeadingIconWrapper>{leadingIcon}</LeadingIconWrapper>}
        {children}
        {label && (
          <InputLabel
            $defaultLabelTopPos={defaultLabelTopPos}
            $withLeadingIcon={!!leadingIcon}
            $withTrailingIcon={!!trailingIcon}
            $hasError={!!error}
            $hasContent={hasContent}
            disabled={disabled}
            htmlFor={id}
            id={labelId}
          >
            {label}
          </InputLabel>
        )}
        {trailingIcon && <TrailingIconWrapper>{trailingIcon}</TrailingIconWrapper>}
      </InputWrapperStyles>
      {error || warning || helperText || characterCounterSlot ? (
        <BottomRow $alignRight={!error && !warning && !helperText}>
          {error && (
            <InputError>
              <IconWrapper aria-hidden>
                <IconTriangleWarning $size="xxs" $color="DANGER_700" />
              </IconWrapper>
              {error}
            </InputError>
          )}
          {warning && (
            <InputWarning>
              <IconWrapper aria-hidden>
                <IconTriangleWarning $size="xxs" $color="WARNING_700" />
              </IconWrapper>
              <span>{warning}</span>
            </InputWarning>
          )}
          {showHelperText && <HelperText>{helperText}</HelperText>}
          {characterCounterSlot}
        </BottomRow>
      ) : null}
    </ComponentWrapper>
  );
};

const InputWithRef = (
  {
    id,
    type = 'text',
    label,
    value: propValue,
    error,
    warning,
    helperText,
    leadingIcon,
    trailingIcon,
    disabled,
    skipMargin,
    onChange: propOnChange,
    ...props
  }: InputBaseProps,
  forwardedRef: ForwardedRef<HTMLInputElement>,
) => {
  const generatedId = useId();
  id ||= generatedId;

  /** To be able to style the label based on value with an uncontrolled component, we still need a state */
  const [localValue, setLocalValue] = useState('');

  const value = propValue ?? localValue;
  const onChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    e => {
      propOnChange?.(e);
      setLocalValue(e.target.value);
    },
    [propOnChange],
  );

  return (
    <InputWrapper
      id={id}
      label={label}
      hasContent={!!value}
      error={error}
      warning={warning}
      helperText={helperText}
      leadingIcon={leadingIcon}
      trailingIcon={trailingIcon}
      skipMargin={skipMargin}
      disabled={disabled}
    >
      <StyledInput
        {...props}
        onChange={onChange}
        $withLeadingIcon={!!leadingIcon}
        $withTrailingIcon={!!trailingIcon}
        type={type}
        value={value}
        id={id}
        disabled={disabled}
        ref={forwardedRef}
      />
    </InputWrapper>
  );
};

export const Input = forwardRef(InputWithRef);
